import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView, Variants } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import SEO from '@/components/common/SEO';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const M = '#06060c';
const D = '#0b0b17';
const G = '#d4a843';
const IN = '#0ea5e9';
const TE = '#14b8a6'; // teal — corporate accent
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger: Variants = { visible: { transition: { staggerChildren: 0.1 } } };

function Section({ children, style, id }: { children: React.ReactNode; style?: React.CSSProperties; id?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.section
      ref={ref}
      id={id}
      variants={stagger}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      style={style}
    >
      {children}
    </motion.section>
  );
}

function AnimSection({ children, style, id }: { children: React.ReactNode; style?: React.CSSProperties; id?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      id={id}
      variants={stagger}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      style={style}
    >
      {children}
    </motion.div>
  );
}

const BENEFITS = [
  {
    icon: '📊',
    title: 'university.corporate.b1Title',
    titleDefault: 'Centralised dashboards',
    desc: 'university.corporate.b1Desc',
    descDefault: 'Track every team member\'s progress, mastery scores, and completed certificates from one admin panel.',
  },
  {
    icon: '🎓',
    title: 'university.corporate.b2Title',
    titleDefault: '44 degrees. One subscription.',
    desc: 'university.corporate.b2Desc',
    descDefault: 'Technology, Business, Leadership, Communication, Health and more — your team learns what your organisation actually needs.',
  },
  {
    icon: '🤖',
    title: 'university.corporate.b3Title',
    titleDefault: 'AI-taught, no instructor scheduling',
    desc: 'university.corporate.b3Desc',
    descDefault: 'No booking trainers, no scheduling conflicts. Still Waters teaches each employee conversationally, on demand, at their level.',
  },
  {
    icon: '🏆',
    title: 'university.corporate.b4Title',
    titleDefault: 'Verifiable credentials',
    desc: 'university.corporate.b4Desc',
    descDefault: 'Every module and degree earns a Still Waters Certificate with a unique verification code — recognised CPD for your records.',
  },
  {
    icon: '💬',
    title: 'university.corporate.b5Title',
    titleDefault: 'Personalised to each learner',
    desc: 'university.corporate.b5Desc',
    descDefault: 'Still Waters adjusts depth and pacing per individual. A data analyst and a designer taking the same degree get different experiences.',
  },
  {
    icon: '💳',
    title: 'university.corporate.b6Title',
    titleDefault: 'Simple, transparent pricing',
    desc: 'university.corporate.b6Desc',
    descDefault: '$39/seat/month with a 10-seat minimum. No hidden fees, no per-course charges — just flat monthly access for every team member.',
  },
];

const USE_CASES = [
  { role: '💻', dept: 'university.corporate.uc1', deptDefault: 'Tech teams', subjects: 'AI & ML, Cloud DevOps, Cybersecurity' },
  { role: '📈', dept: 'university.corporate.uc2', deptDefault: 'Sales & Marketing', subjects: 'Digital Marketing, Leadership, Psychology' },
  { role: '🏢', dept: 'university.corporate.uc3', deptDefault: 'Executive team', subjects: 'Business Strategy, Finance, Leadership' },
  { role: '🤝', dept: 'university.corporate.uc4', deptDefault: 'People & HR', subjects: 'Coaching, Early Education, Emotional Intelligence' },
  { role: '🌍', dept: 'university.corporate.uc5', deptDefault: 'Global teams', subjects: 'Cross-Cultural Comms, Languages, Sociology' },
  { role: '⚕️', dept: 'university.corporate.uc6', deptDefault: 'Healthcare orgs', subjects: 'Health Science, Mental Health, Aged Care' },
];

interface ContactForm {
  name: string;
  email: string;
  company: string;
  seats: string;
  message: string;
}

export default function UniversityCorporatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [form, setForm] = useState<ContactForm>({ name: '', email: '', company: '', seats: '10-25', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Load Playfair for hero headline
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&display=swap';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      await supabase.from('corporate_enquiries').insert({
        name: form.name,
        email: form.email,
        company: form.company,
        seats: form.seats,
        message: form.message,
        user_id: user?.id || null,
        product: 'university',
        created_at: new Date().toISOString(),
      });
      setSubmitted(true);
    } catch (error) {
      // table may not exist yet — still show success to the user
      console.warn('Corporate enquiry insert:', error);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", overflowX: 'hidden' }}>
      <SEO
        title="Corporate Training & Team Upskilling"
        description="Empower your workforce with 44 university-level degrees. AI-taught, on-demand learning for organizations of all sizes. Centralized dashboards and verifiable credentials from $39/seat."
      />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        {/* Ambient blobs */}
        <div style={{ position: 'absolute', top: -160, right: '10%', width: isMobile ? 300 : 500, height: isMobile ? 300 : 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -80, left: '5%', width: isMobile ? 250 : 400, height: isMobile ? 250 : 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <AnimSection style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? '80px 20px 60px' : '120px 24px 80px', textAlign: 'center' }}>
          <motion.div variants={fadeUp} style={{ marginBottom: 24 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(20,184,166,0.1)', border: `1.5px solid rgba(20,184,166,0.3)`,
              borderRadius: 100, padding: '6px 18px',
              fontSize: 12, fontWeight: 700, color: TE, letterSpacing: '0.8px',
              textTransform: 'uppercase'
            }}>
              🏢 {t('university.corporate.badge', 'Still Waters for Organisations')}
            </span>
          </motion.div>

          <motion.h1 variants={fadeUp} style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: isMobile ? '36px' : 'clamp(38px, 6vw, 72px)',
            fontWeight: 900, lineHeight: 1.06,
            letterSpacing: '-1.5px', margin: '0 0 24px', color: '#fff',
          }}>
            {t('university.corporate.headline', 'Upskill your team.\nNot your budget.')}
          </motion.h1>

          <motion.p variants={fadeUp} style={{ fontSize: isMobile ? 16 : 19, color: MU, maxWidth: 600, margin: '0 auto 48px', lineHeight: 1.7, fontWeight: 500 }}>
            {t('university.corporate.sub', '44 university-level degrees taught by AI. One flat price. Verifiable certificates. No scheduling, no instructors, no limits.')}
          </motion.p>

          <motion.div variants={fadeUp} style={{ display: 'flex', gap: 14, flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: 'center' }}>
            <motion.button
              whileHover={{ scale: 1.04, boxShadow: '0 8px 30px rgba(20,184,166,0.35)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: `linear-gradient(135deg, ${TE}, #0d9488)`,
                color: '#fff', border: 'none', borderRadius: 16,
                padding: '18px 40px', fontSize: 16, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(20,184,166,0.25)',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              {t('university.corporate.ctaEnquire', 'Get a Quote')} →
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02, borderColor: 'rgba(255,255,255,0.3)' }}
              onClick={() => navigate('/')}
              style={{
                background: 'transparent', color: TX, border: `1.5px solid ${BR}`,
                borderRadius: 16, padding: '18px 40px', fontSize: 16, fontWeight: 700,
                cursor: 'pointer', transition: 'border-color 0.2s',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Learn More
            </motion.button>
          </motion.div>

          {/* Stat strip */}
          <motion.div variants={fadeUp} style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 24 : 48,
            marginTop: isMobile ? 56 : 72
          }}>
            {[
              { stat: '$39', label: t('university.corporate.stat1', 'per seat / month') },
              { stat: '10+', label: t('university.corporate.stat2', 'seat minimum') },
              { stat: '44', label: t('university.corporate.stat3', 'degrees included') },
              { stat: '∞', label: t('university.corporate.stat4', 'learning hours') },
            ].map(({ stat, label }) => (
              <div key={stat} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 28 : 36, fontWeight: 900, color: TE, letterSpacing: '-0.5px' }}>{stat}</div>
                <div style={{ fontSize: 11, color: MU, marginTop: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              </div>
            ))}
          </motion.div>
        </AnimSection>
      </div>

      {/* ── BENEFITS ─────────────────────────────────────────────────────── */}
      <div style={{ background: D, borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}` }}>
        <AnimSection style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
            <p style={{ fontSize: 12, letterSpacing: '2px', color: TE, fontWeight: 600, marginBottom: 12 }}>
              {t('university.corporate.whyEyebrow', 'WHY STILL WATERS UNIVERSITY')}
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: '#fff', margin: 0 }}>
              {t('university.corporate.whyHeadline', 'Everything you need. Nothing you don\'t.')}
            </h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {BENEFITS.map((b, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{ y: -4, borderColor: 'rgba(20,184,166,0.3)' }}
                style={{
                  background: CA, border: `1px solid ${BR}`,
                  borderRadius: 18, padding: '28px',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: 30, marginBottom: 14 }}>{b.icon}</div>
                <h3 style={{ fontWeight: 700, fontSize: 17, color: '#fff', marginBottom: 10 }}>
                  {t(b.title, b.titleDefault)}
                </h3>
                <p style={{ fontSize: 14, color: MU, lineHeight: 1.7, margin: 0 }}>
                  {t(b.desc, b.descDefault)}
                </p>
              </motion.div>
            ))}
          </div>
        </AnimSection>
      </div>

      {/* ── USE CASES ────────────────────────────────────────────────────── */}
      <AnimSection style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ fontSize: 12, letterSpacing: '2px', color: IN, fontWeight: 600, marginBottom: 12 }}>
            {t('university.corporate.usecaseEyebrow', 'USE CASES')}
          </p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: 700, color: '#fff', margin: 0 }}>
            {t('university.corporate.usecaseHeadline', 'Every department. Every level.')}
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {USE_CASES.map((uc, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ y: -3, borderColor: 'rgba(14,165,233,0.4)' }}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 16, padding: '22px 20px',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 12 }}>{uc.role}</div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 6 }}>
                {t(uc.dept, uc.deptDefault)}
              </div>
              <div style={{ fontSize: 12, color: MU, lineHeight: 1.6 }}>{uc.subjects}</div>
            </motion.div>
          ))}
        </div>
      </AnimSection>

      {/* ── PRICING TABLE ────────────────────────────────────────────────── */}
      <div style={{ background: D, borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}` }}>
        <AnimSection style={{ maxWidth: 800, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
          <motion.div variants={fadeUp}>
            <p style={{ fontSize: 12, letterSpacing: '2px', color: G, fontWeight: 600, marginBottom: 12 }}>
              {t('university.corporate.pricingEyebrow', 'CORPORATE PRICING')}
            </p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>
              {t('university.corporate.pricingHeadline', 'Simple. Scalable. No surprises.')}
            </h2>
          </motion.div>

          <motion.div
            variants={fadeUp}
            style={{
              background: 'rgba(20,184,166,0.06)',
              border: `1.5px solid rgba(20,184,166,0.3)`,
              borderRadius: 24, padding: '48px',
              marginTop: 40,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 72, fontWeight: 900, color: '#fff', letterSpacing: '-2px' }}>$39</span>
              <span style={{ fontSize: 18, color: MU }}>{t('university.corporate.perSeat', '/seat/month')}</span>
            </div>
            <p style={{ color: MU, fontSize: 16, marginBottom: 36 }}>
              {t('university.corporate.minSeats', 'Minimum 10 seats · Billed monthly or annually')}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, textAlign: 'left', marginBottom: 36 }}>
              {[
                t('university.corporate.incl1', 'All 44 degree programmes'),
                t('university.corporate.incl2', 'All 200+ modules & lessons'),
                t('university.corporate.incl3', 'Verifiable PDF certificates'),
                t('university.corporate.incl4', 'Admin dashboard & reporting'),
                t('university.corporate.incl5', 'Bulk enrolment management'),
                t('university.corporate.incl6', 'Dedicated account support'),
              ].map(feat => (
                <div key={feat} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ color: TE, fontWeight: 700, flexShrink: 0 }}>✓</span>
                  <span style={{ fontSize: 14, color: MU, lineHeight: 1.5 }}>{feat}</span>
                </div>
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.04, boxShadow: '0 8px 30px rgba(20,184,166,0.4)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: `linear-gradient(135deg, ${TE}, #0d9488)`,
                color: '#fff', border: 'none', borderRadius: 14,
                padding: '16px 48px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(20,184,166,0.25)',
              }}
            >
              {t('university.corporate.getStarted', 'Get Started')} →
            </motion.button>
          </motion.div>
        </AnimSection>
      </div>

      {/* ── ENQUIRY FORM ─────────────────────────────────────────────────── */}
      <AnimSection id="enquiry-form" style={{ maxWidth: 640, margin: '0 auto', padding: '80px 24px 100px' } as React.CSSProperties}>
        <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 44 }}>
          <p style={{ fontSize: 12, letterSpacing: '2px', color: TE, fontWeight: 600, marginBottom: 12 }}>
            {t('university.corporate.formEyebrow', 'GET IN TOUCH')}
          </p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: '#fff', margin: 0 }}>
            {t('university.corporate.formHeadline', 'Talk to our team')}
          </h2>
          <p style={{ color: MU, fontSize: 15, marginTop: 12 }}>
            {t('university.corporate.formSub', 'We\'ll set up your organisation, onboard your team, and have everyone learning within 24 hours.')}
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              background: 'rgba(74,222,128,0.08)', border: '1.5px solid rgba(74,222,128,0.3)',
              borderRadius: 20, padding: '48px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 52, marginBottom: 20 }}>🎉</div>
            <h3 style={{ fontWeight: 700, fontSize: 22, marginBottom: 12, color: '#4ade80' }}>
              {t('university.corporate.submitted', 'Enquiry received!')}
            </h3>
            <p style={{ color: MU, fontSize: 15, lineHeight: 1.7 }}>
              {t('university.corporate.submittedSub', 'We\'ll reach out within 24 hours to discuss your team\'s needs and get you set up.')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            variants={fadeUp}
            style={{
              background: CA, border: `1px solid ${BR}`,
              borderRadius: 20, padding: '40px',
            }}
          >
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {[
                { label: t('university.corporate.fieldName', 'Your name'), key: 'name', type: 'text', placeholder: 'Sarah Johnson' },
                { label: t('university.corporate.fieldEmail', 'Work email'), key: 'email', type: 'email', placeholder: 'sarah@company.com' },
                { label: t('university.corporate.fieldCompany', 'Company name'), key: 'company', type: 'text', placeholder: 'Acme Corp' },
              ].map(field => (
                <div key={field.key}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    value={form[field.key as keyof ContactForm]}
                    onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    required
                    style={{
                      width: '100%', padding: '13px 16px',
                      background: 'rgba(255,255,255,0.04)',
                      border: `1.5px solid ${BR}`,
                      borderRadius: 11, color: TX, fontSize: 15, outline: 'none',
                      boxSizing: 'border-box',
                      fontFamily: "'Inter', sans-serif",
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => (e.target.style.borderColor = TE)}
                    onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.07)')}
                  />
                </div>
              ))}

              {/* Seat count selector */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                  {t('university.corporate.fieldSeats', 'Number of seats')}
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['10-25', '26-50', '51-100', '100+'].map(range => (
                    <motion.button
                      key={range}
                      type="button"
                      whileHover={{ scale: 1.04 }}
                      onClick={() => setForm(prev => ({ ...prev, seats: range }))}
                      style={{
                        background: form.seats === range ? 'rgba(20,184,166,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1.5px solid ${form.seats === range ? 'rgba(20,184,166,0.5)' : BR}`,
                        borderRadius: 10, padding: '10px 20px',
                        cursor: 'pointer', color: form.seats === range ? TE : MU,
                        fontSize: 14, fontWeight: 600, transition: 'all 0.15s',
                      }}
                    >
                      {range} {t('university.corporate.seats', 'seats')}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
                  {t('university.corporate.fieldMessage', 'Anything else we should know?')} ({t('university.corporate.optional', 'optional')})
                </label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={t('university.corporate.messagePlaceholder', 'e.g. We want to focus on AI upskilling for our engineering team…')}
                  rows={3}
                  style={{
                    width: '100%', padding: '13px 16px',
                    background: 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${BR}`,
                    borderRadius: 11, color: TX, fontSize: 15, outline: 'none',
                    boxSizing: 'border-box', resize: 'vertical',
                    fontFamily: "'Inter', sans-serif",
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.target.style.borderColor = TE)}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.07)')}
                />
              </div>

              {err && (
                <div style={{ color: '#f87171', fontSize: 13, background: 'rgba(248,113,113,0.1)', borderRadius: 9, padding: '10px 14px' }}>
                  {err}
                </div>
              )}

              <motion.button
                type="submit"
                disabled={submitting}
                whileHover={{ scale: 1.02, boxShadow: '0 6px 24px rgba(20,184,166,0.35)' }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: `linear-gradient(135deg, ${TE}, #0d9488)`,
                  color: '#fff', border: 'none', borderRadius: 12,
                  padding: '15px', fontSize: 16, fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                  boxShadow: '0 4px 20px rgba(20,184,166,0.25)',
                  marginTop: 4,
                }}
              >
                {submitting
                  ? t('university.corporate.submitting', 'Sending…')
                  : t('university.corporate.submit', 'Send Enquiry')} →
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimSection>

    </div>
  );
}
